import { ResourceType } from '@cotera/api';
import {
  artifactTypeAsResourceType,
  resourceIdFromArtifactPayload,
  type ArtifactDescriptor,
} from '@cotera/client/v0/chat/artifacts';
import type { InitialArtifactParams } from '@cotera/sdk/core';

export type ShareableResourceTarget = {
  type: ResourceType;
  id: string;
  name?: string;
};

type ArtifactLike = Pick<ArtifactDescriptor, 'type' | 'artifactId' | 'payload'>;

type FolderArtifactPayload = { folderId?: string; name?: string };

/**
 * A folder artifact carries its name in the payload, so a folder can be listed
 * by name before its resource is fetched. No other type does.
 */
function getArtifactDisplayName(artifact: ArtifactLike): string | undefined {
  if (artifact.type !== ResourceType.Folder) {
    return undefined;
  }
  const name = (artifact.payload as FolderArtifactPayload | null)?.name?.trim();
  return name !== undefined && name !== '' ? name : undefined;
}

/**
 * The workspace resource an artifact stands for, when it stands for one. Panel-
 * local artifacts (charts, workflow steps) have no resource to share.
 */
export function artifactToShareableResource(
  artifact: ArtifactLike
): ShareableResourceTarget | null {
  const type = artifactTypeAsResourceType(artifact.type);
  if (type === null) {
    return null;
  }
  const id = resourceIdFromArtifactPayload(
    artifact.type,
    artifact.payload,
    artifact.artifactId
  );
  if (id === null) {
    return null;
  }
  return {
    type,
    id,
    name: getArtifactDisplayName(artifact),
  };
}

function initialArtifactToDescriptor(
  artifact: InitialArtifactParams
): ArtifactLike {
  return {
    type: artifact.artifactType,
    artifactId: artifact.artifactId ?? '',
    payload: artifact.data ?? null,
  };
}

export function collectShareableChatResources(params: {
  conversationId: string;
  chatDisplayName?: string;
  artifacts: readonly ArtifactLike[];
  /**
   * Artifacts the chat was seeded with, read **only** while {@link seedActive}.
   *
   * This is a snapshot taken when the chat was seeded and held for the session;
   * nothing ever subtracts from it. That is fine for the window it exists for —
   * a chat seeded from a resource page has not round-tripped yet, so `artifacts`
   * is empty and the modal would otherwise offer nothing — and wrong after it,
   * because by then `artifacts` is authoritative and an artifact missing from it
   * is missing *because the user removed it from the chat*. Reading the seed
   * past that window puts a resource in the share modal that the panel says is
   * gone.
   */
  deferredSeedArtifacts?: readonly InitialArtifactParams[];
  /**
   * Whether the seed still owns the artifacts — i.e. the conversation has not
   * taken them over yet. Gates {@link deferredSeedArtifacts}; defaults to false,
   * so the snapshot is opt-in rather than something a caller inherits by
   * forgetting.
   */
  seedActive?: boolean;
  /** When false, omit the chat (e.g. seed shell before the first message). Defaults to true. */
  includeChat?: boolean;
}): ShareableResourceTarget[] {
  const seen = new Set<string>();
  const results: ShareableResourceTarget[] = [];

  const add = (resource: ShareableResourceTarget): void => {
    const key = `${resource.type}:${resource.id}`;
    if (seen.has(key) === true) {
      return;
    }
    seen.add(key);
    results.push(resource);
  };

  if (params.includeChat !== false) {
    add({
      type: ResourceType.Chat,
      id: params.conversationId,
      ...(params.chatDisplayName !== undefined &&
        params.chatDisplayName.trim() !== '' && {
          name: params.chatDisplayName.trim(),
        }),
    });
  }

  for (const artifact of params.artifacts) {
    const resource = artifactToShareableResource(artifact);
    if (resource !== null) {
      add(resource);
    }
  }

  if (
    params.seedActive === true &&
    params.deferredSeedArtifacts !== undefined
  ) {
    for (const artifact of params.deferredSeedArtifacts) {
      const resource = artifactToShareableResource(
        initialArtifactToDescriptor(artifact)
      );
      if (resource !== null) {
        add(resource);
      }
    }
  }

  return results;
}

export function shareableResourceKey(
  resource: ShareableResourceTarget
): string {
  return `${resource.type}:${resource.id}`;
}
